var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "theme-ui/jsx-runtime";
// @ts-check
import { Box, Flex, Label, P, Text, Title, } from '@bottlebooks/gatsby-design-system';
import React from 'react';
import Button from '../Button/Button';
/**
 *
 * @param {object} props
 * @param {boolean} [props.indeterminate]
 * @param {boolean} [props.checked]
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.readOnly]
 * @param {string} [props.className]
 * @param {(Event) => void} [props.onChange]
 */
export function Checkbox(_a) {
    var { indeterminate = false, checked, onChange } = _a, rest = __rest(_a, ["indeterminate", "checked", "onChange"]);
    return (_jsx("input", Object.assign({ type: "checkbox", checked: checked, onChange: onChange }, rest, { ref: (el) => el && (el.indeterminate = indeterminate) })));
}
/**
 * A container enclosing the complete DataTable.
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {string} [props.title]
 * @param {string} [props.description]
 * @param {boolean} [props.scroll] Makes area scrollable. Parent should be height: 100% to work.
 * @returns {React.ReactElement}
 */
export function TableContainer(_a) {
    var { children, title, description, scroll, 
    // @ts-ignore
    onInputChange } = _a, // Remove the invalid prop from the rest.
    rest = __rest(_a, ["children", "title", "description", "scroll", "onInputChange"]);
    return (_jsxs(Box, Object.assign({ sx: Object.assign({ position: 'relative' }, (scroll && { height: '100%', overflow: 'scroll' })) }, rest, { children: [title && _jsx(Title, { children: title }), description && _jsx(P, { children: description }), children] })));
}
export default TableContainer;
export function TableToolbar(_a) {
    var { children } = _a, rest = __rest(_a, ["children"]);
    return (_jsx(Box, Object.assign({ sx: { display: 'flex' } }, rest, { children: children })));
}
/**
 *
 * @param {object} props
 * @param {number} props.totalSelected The number of all selected rows (not only on the current page).
 * @param {React.ReactNode} [props.children] The individual batch actions.
 * @param {boolean} [props.checked]
 * @param {()=>void} [props.onChange]
 * @param {boolean} [props.indeterminate]
 * @param {object[]} [props.selectedRows]
 */
export function TableBatchActions(_a) {
    var { children, totalSelected, checked, onChange, indeterminate } = _a, rest = __rest(_a, ["children", "totalSelected", "checked", "onChange", "indeterminate"]);
    // TODO Remove the checkbox if selection is not allowed.
    return (_jsxs(Box, Object.assign({ sx: {
            alignItems: 'center',
            backgroundColor: 'accent',
            color: 'white',
            zIndex: 2,
            width: '100%',
            padding: 2,
        } }, rest, { children: [_jsx(Checkbox, { onChange: onChange, checked: checked, indeterminate: indeterminate, sx: {
                    '&&': { margin: 2 }, // FIXME Bootstrap style spill.
                } }), totalSelected && (_jsxs(Text, Object.assign({ variant: "small", sx: { paddingX: 2 } }, { children: [totalSelected, " items selected"] }))), children] })));
}
export function TableBatchAction(_a) {
    var { children } = _a, rest = __rest(_a, ["children"]);
    return (_jsx(Box, Object.assign({ sx: { paddingX: 2 } }, { children: _jsx(Button, Object.assign({ variant: "text", sx: {
                fontWeight: 'bold',
                transition: 'backgroundColor',
                ':hover': { backgroundColor: 'primary', color: 'onPrimary' },
                ':focus': { color: 'onPrimary' }, // fixes Bootstrap style spill from a:focus.
            } }, rest, { children: children })) })));
}
export function TableToolbarContent(_a) {
    var { children } = _a, rest = __rest(_a, ["children"]);
    return (_jsx(Box, Object.assign({ sx: { display: 'flex', width: '100%', justifyContent: 'flex-end' } }, rest, { children: children })));
}
export function TableToolbarSearch(_a) {
    var { children } = _a, rest = __rest(_a, ["children"]);
    return (_jsx(Box, Object.assign({ sx: {} }, rest, { children: children })));
}
export function TableToolbarMenu(_a) {
    var { children } = _a, rest = __rest(_a, ["children"]);
    return (_jsx(Box, Object.assign({ sx: {} }, rest, { children: children })));
}
export function TableToolbarAction(_a) {
    var { children } = _a, rest = __rest(_a, ["children"]);
    return (_jsx(Box, Object.assign({ sx: { display: 'none' } }, rest, { children: children })));
}
export function Table(_a) {
    var { children } = _a, rest = __rest(_a, ["children"]);
    return (_jsx(Box, Object.assign({ as: "table", sx: { borderCollapse: 'collapse' } }, rest, { children: children })));
}
export function TableHead(_a) {
    var { children } = _a, rest = __rest(_a, ["children"]);
    return (_jsx(Box, Object.assign({ as: "thead", sx: {} }, rest, { children: children })));
}
/**
 * @type {React.FC<{children?: React.ReactNode}>}
 */
export const TableRow = React.memo(function TableRow(_a) {
    var { children } = _a, rest = __rest(_a, ["children"]);
    return (_jsx(Box, Object.assign({ as: "tr", sx: {
            borderBottom: 1,
            borderColor: 'border',
            ':hover .bbSelectRow': { visibility: 'visible' },
            ':last-of-type': { borderBottom: 0 },
        } }, rest, { children: children })));
});
export function TableHeader(_a) {
    var { children } = _a, rest = __rest(_a, ["children"]);
    return (_jsx(Label, Object.assign({ as: "th", variant: "small", sx: {
            textAlign: 'left',
            color: 'lightText',
            backgroundColor: 'white',
            paddingX: 2,
            paddingY: 3,
            borderBottom: 1,
            borderColor: 'border',
            zIndex: 'header', // This is only necessary within the Bottlebooks dashboard. It allows sticky header to stay on top.
        } }, rest, { children: children })));
}
export function TableBody(_a) {
    var { children } = _a, rest = __rest(_a, ["children"]);
    return (_jsx(Box, Object.assign({ as: "tbody", sx: {} }, rest, { children: children })));
}
/**
 * The "select all" header cell to select all rows.
 * @param {object} props
 * @param {(boolean)=>void} props.onChange The handler to select/unselect all rows.
 * @param {boolean} props.checked Are all rows selected?
 * @param {boolean} [props.disabled] Is the selection disabled?
 * @param {boolean} [props.indeterminate] Are some of the rows selected, some not?
 */
export function TableSelectAll(_a) {
    var { onChange, checked, indeterminate, disabled } = _a, rest = __rest(_a, ["onChange", "checked", "indeterminate", "disabled"]);
    return (_jsx(TableHeader, Object.assign({ sx: { width: 40, textAlign: 'center' } }, rest, { children: _jsx(Flex, Object.assign({ as: "label", align: "center", justify: "center", sx: {
                position: 'absolute',
                top: 0,
                bottom: 0,
                right: 0,
                left: 0,
            } }, { children: _jsx(Checkbox, { onChange: onChange, checked: checked, disabled: disabled, indeterminate: indeterminate }) })) })));
}
/**
 * The "select" cell to select a single row.
 * @param {object} props
 * @param {(boolean)=>void} props.onChange The handler to select/unselect the row.
 * @param {boolean} props.checked Is the row selected?
 * @param {boolean} [props.disabled] Is the selection disabled?
 * @param {boolean} [props.indeterminate] Is the row selection in an indeterminate state?
 * @param {boolean} [props.checkboxIsHidden] Hides the checkbox visually.
 */
export function TableSelectRow(_a) {
    var { onChange, checked, indeterminate, disabled, checkboxIsHidden } = _a, rest = __rest(_a, ["onChange", "checked", "indeterminate", "disabled", "checkboxIsHidden"]);
    return (
    //@ts-ignore - sx not on IntrinsicAttributes
    _jsx(TableCell, Object.assign({ sx: { width: 40, textAlign: 'center' } }, rest, { children: _jsx(Flex, Object.assign({ as: "label", align: "center", justify: "center", sx: {
                position: 'absolute',
                top: 0,
                bottom: 0,
                right: 0,
                left: 0,
            } }, { children: _jsx(Checkbox, { onChange: onChange, checked: checked, disabled: disabled, indeterminate: indeterminate, className: "bbSelectRow", sx: checkboxIsHidden && !checked ? { visibility: 'hidden' } : undefined }) })) })));
}
/**
 *
 * @param {object} props
 * @param {React.ReactNode} [props.children]
 * @param {()=>void} [props.onClick]
 * @param {string} [props.colSpan]
 */
export function TableCell(_a) {
    var { children } = _a, rest = __rest(_a, ["children"]);
    return (_jsx(Text, Object.assign({ as: "td", variant: "small", sx: { paddingX: 2, paddingY: 1 } }, rest, { children: children })));
}
export function TableFoot(_a) {
    var { children } = _a, rest = __rest(_a, ["children"]);
    return (_jsx(Box, Object.assign({ as: "tfoot", sx: {} }, rest, { children: children })));
}
TableContainer.TableToolbar = TableToolbar;
TableContainer.TableBatchActions = TableBatchActions;
TableContainer.TableBatchAction = TableBatchAction;
TableContainer.TableToolbarContent = TableToolbarContent;
TableContainer.TableToolbarSearch = TableToolbarSearch;
TableContainer.TableToolbarMenu = TableToolbarMenu;
TableContainer.TableToolbarAction = TableToolbarAction;
TableContainer.Table = Table;
TableContainer.TableHead = TableHead;
TableContainer.TableRow = TableRow;
TableContainer.TableSelectAll = TableSelectAll;
TableContainer.TableHeader = TableHeader;
TableContainer.TableBody = TableBody;
TableContainer.TableSelectRow = TableSelectRow;
TableContainer.TableCell = TableCell;
TableContainer.TableFoot = TableFoot;
//# sourceMappingURL=DataTable.js.map